/**
 * Calibrările salvate pe mașină în IndexedDB, plus export/import ca fișier JSON cu checksum (SHA-256),
 * ca un fișier modificat din greșeală să fie refuzat la import.
 */
import type { Ecu, Parts } from './types';

export interface Calibration { carId: string; name: string; ecu: Ecu; parts: Parts; savedAt: string }

const DB = 'nume-joc', STORE = 'calibrari';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: 'carId' });
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

export async function saveCalibration(c: Calibration): Promise<void> {
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(c);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function loadCalibration(carId: string): Promise<Calibration | null> {
  try {
    const db = await open();
    const c = await new Promise<Calibration | null>((resolve, reject) => {
      const r = db.transaction(STORE).objectStore(STORE).get(carId);
      r.onsuccess = () => resolve((r.result as Calibration) ?? null);
      r.onerror = () => reject(r.error);
    });
    db.close();
    return c;
  } catch {
    return null;
  }
}

export async function deleteCalibration(carId: string): Promise<void> {
  const db = await open();
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(carId);
    tx.oncomplete = () => resolve();
  });
  db.close();
}

async function sha256(text: string): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Exportă calibrarea ca fișier: prin Web Share pe iPhone, altfel ca descărcare. */
export async function exportCalibration(c: Calibration): Promise<void> {
  const body = JSON.stringify({ format: 'nume-joc-calibrare', version: 1, ...c });
  const file = JSON.stringify({ checksum: await sha256(body), data: JSON.parse(body) }, null, 2);
  const name = `${c.carId}-${c.name.replace(/[^a-z0-9-]+/gi, '_')}.json`;
  const blob = new Blob([file], { type: 'application/json' });
  const f = new File([blob], name, { type: 'application/json' });
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
  if (nav.canShare?.({ files: [f] })) {
    try { await navigator.share({ files: [f], title: name }); return; } catch { /* anulat: descărcare */ }
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export async function importCalibration(text: string, carId: string): Promise<Calibration> {
  const f = JSON.parse(text) as { checksum: string; data: Calibration & { format: string } };
  if (!f.data || f.data.format !== 'nume-joc-calibrare') throw new Error('nu e un fișier de calibrare');
  if ((await sha256(JSON.stringify(f.data))) !== f.checksum) throw new Error('checksum greșit: fișierul a fost modificat');
  if (f.data.carId !== carId) throw new Error(`calibrarea e pentru ${f.data.carId}, nu pentru mașina asta`);
  return { carId: f.data.carId, name: f.data.name, ecu: f.data.ecu, parts: f.data.parts, savedAt: f.data.savedAt };
}
